import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { ArrowLeft, Stethoscope, Clock, Award, Phone, MessageCircle, Loader2, Newspaper } from 'lucide-react';

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const blocks = [];
  let list = null; let para = [];
  const flushPara = () => { if (para.length) { blocks.push({ type: 'p', text: para.join(' ') }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ type: 'ul', items: list }); list = null; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(3) }); continue; }
    if (line.startsWith('### ')) { flushPara(); flushList(); blocks.push({ type: 'h3', text: line.slice(4) }); continue; }
    if (line.startsWith('# ')) { flushPara(); flushList(); blocks.push({ type: 'h2', text: line.slice(2) }); continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) { flushPara(); if (!list) list = []; list.push(line.slice(2)); continue; }
    flushList(); para.push(line);
  }
  flushPara(); flushList();
  const inline = (t) => {
    const parts = t.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>);
  };
  return blocks.map((b, i) => {
    if (b.type === 'h2') return <h2 key={i} className="text-2xl sm:text-3xl font-medium text-slate-900 mt-10 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{inline(b.text)}</h2>;
    if (b.type === 'h3') return <h3 key={i} className="text-xl font-medium text-slate-900 mt-6 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{inline(b.text)}</h3>;
    if (b.type === 'ul') return <ul key={i} className="list-disc pl-6 my-4 space-y-2 text-slate-700">{b.items.map((li, j) => <li key={j}>{inline(li)}</li>)}</ul>;
    return <p key={i} className="text-slate-700 leading-relaxed my-4">{inline(b.text)}</p>;
  });
}

export default function DoctorProcedurePage() {
  const { slug, procedureSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [related, setRelated] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/public/doctor/${slug}/procedures/${procedureSlug}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || 'Page not found'); });
    api.post('/track/event', { doctor_slug: slug, event_type: 'page_view', referrer: document.referrer || '' }).catch(() => {});
    api.get(`/public/doctor/${slug}/related-content`)
      .then(r => { if (!cancelled) setRelated(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug, procedureSlug]);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
        <div>
          <p className="text-slate-500 mb-4">{error}</p>
          <Link to={`/dr/${slug}`} className="text-sky-600 hover:text-sky-700 text-sm font-medium">Back to doctor profile</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-400"><Loader2 size={18} className="animate-spin mr-2" /> Loading…</div>;
  }

  const { page, procedure, doctor } = data;
  const canonical = `${window.location.origin}/dr/${slug}/procedures/${procedureSlug}`;

  const medicalProcedureLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: procedure.name,
    description: procedure.description,
    procedureType: procedure.type,
    performer: {
      '@type': 'Physician', name: doctor.name,
      url: `${window.location.origin}/dr/${doctor.slug}`,
      image: doctor.photo_url || undefined,
      telephone: doctor.phone || undefined,
    },
    url: canonical,
  };
  const faqLd = (page.faq && page.faq.length > 0) ? {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: page.faq.map(f => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
  } : null;
  // Determine primary specialty for reciprocal "collection" link back to /for/{specialty}
  const primarySpecialtySlug = (doctor.specializations && doctor.specializations[0]) || null;
  const primarySpecialtyName = (doctor.specialization_names && doctor.specialization_names[0]) || null;

  const breadcrumbLd = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'DocSite', item: `${window.location.origin}/` },
      ...(primarySpecialtySlug && primarySpecialtyName ? [{ '@type': 'ListItem', position: 2, name: primarySpecialtyName, item: `${window.location.origin}/for/${primarySpecialtySlug}` }] : []),
      { '@type': 'ListItem', position: primarySpecialtySlug ? 3 : 2, name: doctor.name, item: `${window.location.origin}/dr/${doctor.slug}` },
      { '@type': 'ListItem', position: primarySpecialtySlug ? 4 : 3, name: procedure.name, item: canonical },
    ],
  };

  const phoneClick = () => {
    api.post('/track/event', { doctor_slug: slug, event_type: 'phone_click' }).catch(() => {});
  };
  const waClick = () => {
    api.post('/track/event', { doctor_slug: slug, event_type: 'whatsapp_click' }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="doctor-procedure-page">
      <Helmet>
        <title>{page.meta_title}</title>
        <meta name="description" content={page.meta_description} />
        {page.keywords?.length > 0 && <meta name="keywords" content={page.keywords.join(', ')} />}
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={page.meta_title} />
        <meta property="og:description" content={page.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {doctor.photo_url && <meta property="og:image" content={doctor.photo_url} />}
        <script type="application/ld+json">{JSON.stringify(medicalProcedureLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}
      </Helmet>

      {/* Top nav */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(`/dr/${slug}`)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600" data-testid="back-to-doctor-btn">
            <ArrowLeft size={14} />Back to {doctor.name}
          </button>
          <a href={`/dr/${slug}#book`} className="text-xs font-medium px-4 py-1.5 bg-sky-600 text-white rounded-full hover:bg-sky-700" data-testid="book-cta-top">
            Book appointment
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3">
            <Stethoscope size={12} /> {procedure.type || 'Procedure'} · {page.city}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="procedure-page-h1">
            {page.h1}
          </h1>
          {primarySpecialtySlug && primarySpecialtyName && (
            <Link
              to={`/for/${primarySpecialtySlug}`}
              className="inline-flex items-center gap-1.5 mt-5 text-xs text-slate-500 hover:text-sky-700 transition-colors"
              data-testid="procedure-specialty-collection-link"
            >
              <span className="w-[6px] h-[6px] rounded-full bg-sky-500 opacity-60" aria-hidden="true" />
              Part of DocSite's {primarySpecialtyName} collection <span aria-hidden="true">→</span>
            </Link>
          )}
          <div className="flex items-center gap-3 mt-6">
            {doctor.photo_url && <img src={doctor.photo_url} alt={doctor.name} className="w-12 h-12 rounded-full object-cover border border-slate-200" />}
            <div>
              <p className="text-sm font-medium text-slate-900">{doctor.name}</p>
              <p className="text-xs text-slate-500">{doctor.qualifications}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-600">
            {doctor.experience_years > 0 && (
              <span className="inline-flex items-center gap-1.5"><Award size={14} className="text-amber-500" /> {doctor.experience_years}+ years</span>
            )}
            {procedure.duration_minutes && (
              <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-sky-600" /> {procedure.duration_minutes} min</span>
            )}
            {doctor.consultation_fee && (
              <span className="inline-flex items-center gap-1.5">₹ {doctor.consultation_fee} consultation fee</span>
            )}
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 prose-article" data-testid="procedure-page-body">
        {renderMarkdown(page.intro_content)}

        {/* Indications strip */}
        {procedure.indications && (
          <div className="mt-10 p-5 bg-slate-50 border border-slate-200 rounded-2xl">
            <p className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-2">Common indications</p>
            <p className="text-sm text-slate-700">{procedure.indications}</p>
          </div>
        )}
      </article>

      {/* FAQ */}
      {page.faq && page.faq.length > 0 && (
        <section className="py-12 md:py-14 bg-slate-50/60 border-y border-slate-100" data-testid="procedure-faq">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <h2 className="text-2xl sm:text-3xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {page.faq.map((f, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5">
                  <h3 className="font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.question}</h3>
                  <p className="text-slate-700 text-sm leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10 bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-100 rounded-2xl p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Consult {doctor.name} about {procedure.name}
          </h3>
          <p className="text-sm text-slate-600 mb-5">Talk directly to your specialist. No middlemen.</p>
          <div className="flex flex-wrap gap-3">
            <Link to={`/dr/${slug}#book`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-600 text-white rounded-xl text-sm font-medium hover:bg-sky-700" data-testid="procedure-book-btn">
              Book appointment
            </Link>
            {doctor.phone && (
              <a href={`tel:${doctor.phone}`} onClick={phoneClick} className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50" data-testid="procedure-call-btn">
                <Phone size={14} /> Call
              </a>
            )}
            {doctor.phone && (
              <a href={`https://wa.me/${doctor.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi Dr. ${doctor.name}, I'm interested in ${procedure.name}`)}`} target="_blank" rel="noopener noreferrer" onClick={waClick} className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700" data-testid="procedure-wa-btn">
                <MessageCircle size={14} /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Related Content — Internal Linking for SEO */}
      {related && (
        <RelatedContentSection
          related={related}
          currentSlug={procedureSlug}
          currentType="procedure"
          doctorSlug={slug}
          doctorName={doctor.name}
        />
      )}

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-400">
        Published on <Link to={`/dr/${slug}`} className="text-sky-600 hover:underline">{doctor.name}'s DocSite</Link>
      </footer>
    </div>
  );
}


function RelatedContentSection({ related, currentSlug, currentType, doctorSlug, doctorName }) {
  const otherProcedures = (related.procedures || []).filter(p => p.procedure_slug !== currentSlug).slice(0, 4);
  const articles = (related.articles || []).slice(0, 4);

  if (!otherProcedures.length && !articles.length) return null;

  return (
    <section className="py-12 bg-slate-50/60 border-y border-slate-100" data-testid="related-content-section">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <h2 className="text-xl sm:text-2xl font-medium text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          More from {doctorName}
        </h2>

        {otherProcedures.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
              <Stethoscope size={12} /> Other Procedures
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {otherProcedures.map(p => (
                <Link key={p.procedure_slug} to={`/dr/${doctorSlug}/procedures/${p.procedure_slug}`}
                  className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-200 hover:shadow-sm transition-all"
                  data-testid={`related-proc-${p.procedure_slug}`}>
                  <p className="text-sm font-medium text-slate-900">{p.procedure_name}</p>
                  {p.meta_description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.meta_description}</p>}
                </Link>
              ))}
            </div>
          </div>
        )}

        {articles.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
              <Newspaper size={12} /> Articles by {doctorName}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {articles.map(a => (
                <Link key={a.slug} to={`/dr/${doctorSlug}/articles/${a.slug}`}
                  className="block bg-white rounded-xl border border-slate-200 p-4 hover:border-sky-200 hover:shadow-sm transition-all"
                  data-testid={`related-article-${a.slug}`}>
                  <p className="text-sm font-medium text-slate-900">{a.title}</p>
                  {a.meta_description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.meta_description}</p>}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
